import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Tabs,
  Tab,
  Card,
  CardContent,
  Grid,
  Chip,
  Avatar,
  Button,
  IconButton,
  Divider,
  Breadcrumbs,
  Link,
} from '@mui/material';
import {
  ArrowBack,
  Edit,
  Assignment,
  Add,
  ChevronRight,
  Science as ScienceIcon,
  Code as CodeIcon,
  BugReport as BugReportIcon,
  Build as BuildIcon,
  School as SchoolIcon,
  Work as WorkIcon,
  SportsEsports as GameIcon,
  MusicNote as MusicIcon,
  Movie as MovieIcon,
  Groups as GroupsIcon,
} from '@mui/icons-material';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`testeabas-tabpanel-${index}`}
      aria-labelledby={`testeabas-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3, minHeight: '400px' }}>
          {children}
        </Box>
      )}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `testeabas-tab-${index}`,
    'aria-controls': `testeabas-tabpanel-${index}`,
  };
}

const TesteAbasPage: React.FC = () => {
  const navigate = useNavigate();
  const [tabValue, setTabValue] = useState(0);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleBack = () => {
    navigate('/dashboard');
  };

  // Dados de exemplo para cada aba
  const tabData = [
    {
      title: 'Teste Abas 1',
      icon: <ScienceIcon />,
      content: 'Conteúdo da primeira aba de teste',
      color: 'primary',
      description: 'Esta é a primeira aba de teste com conteúdo científico'
    },
    {
      title: 'Teste Abas 2',
      icon: <CodeIcon />,
      content: 'Conteúdo da segunda aba de teste',
      color: 'secondary',
      description: 'Esta é a segunda aba de teste com conteúdo de programação e desenvolvimento'
    },
    {
      title: 'Teste Abas 3',
      icon: <BugReportIcon />,
      content: 'Conteúdo da terceira aba de teste',
      color: 'error',
      description: 'Esta é a terceira aba de teste com conteúdo de debugging'
    },
    {
      title: 'Teste Abas 4',
      icon: <BuildIcon />,
      content: 'Conteúdo da quarta aba de teste',
      color: 'warning',
      description: 'Esta é a quarta aba de teste com conteúdo de construção'
    },
    {
      title: 'Teste Abas 5',
      icon: <SchoolIcon />,
      content: 'Conteúdo da quinta aba de teste',
      color: 'info',
      description: 'Esta é a quinta aba de teste com conteúdo educacional'
    },
    {
      title: 'Teste Abas 6',
      icon: <WorkIcon />,
      content: 'Conteúdo da sexta aba de teste',
      color: 'success',
      description: 'Esta é a sexta aba de teste com conteúdo profissional'
    },
    {
      title: 'Teste Abas 7',
      icon: <GameIcon />,
      content: 'Conteúdo da sétima aba de teste',
      color: 'primary',
      description: 'Esta é a sétima aba de teste com conteúdo de jogos'
    },
    {
      title: 'Teste Abas 8',
      icon: <MusicIcon />,
      content: 'Conteúdo da oitava aba de teste',
      color: 'secondary',
      description: 'Esta é a oitava aba de teste com conteúdo musical'
    },
    {
      title: 'Teste Abas 9',
      icon: <MovieIcon />,
      content: 'Conteúdo da nona aba de teste',
      color: 'error',
      description: 'Esta é a nona aba de teste com conteúdo cinematográfico'
    },
    {
      title: 'Teste Abas 10',
      icon: <GroupsIcon />,
      content: 'Conteúdo da décima aba de teste',
      color: 'warning',
      description: 'Esta é a décima aba de teste com conteúdo social'
    }
  ];

  return (
    <>
      <Helmet>
        <title>Teste Abas - SysUp</title>
      </Helmet>

      <Box>
        {/* Header da Página */}
        <Box sx={{ mb: 3 }}>
          {/* Breadcrumbs */}
          <Breadcrumbs 
            aria-label="breadcrumb" 
            sx={{ mb: 2 }}
            separator={<ChevronRight fontSize="small" />}
          >
            <Link
              underline="hover"
              color="inherit"
              href="#"
              onClick={(e) => {
                e.preventDefault();
                navigate('/dashboard');
              }}
            >
              Dashboard
            </Link>
            <Typography color="text.primary">Teste Abas</Typography>
          </Breadcrumbs>

          {/* Header Principal */}
          <Paper elevation={1} sx={{ p: 1.5, mb: 1, borderRadius: 3 }}>
            <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={2}>
              {/* Info Principal */}
              <Box display="flex" alignItems="center" gap={2}>
                <IconButton 
                  onClick={handleBack} 
                  color="primary"
                  sx={{
                    bgcolor: 'white',
                    boxShadow: 1,
                    '&:hover': {
                      bgcolor: 'primary.50',
                      transform: 'translateY(-1px)',
                      boxShadow: 2
                    },
                    transition: 'all 0.2s ease'
                  }}
                >
                  <ArrowBack />
                </IconButton>
                <Avatar 
                  sx={{ 
                    width: 60, 
                    height: 60, 
                    bgcolor: 'primary.main',
                    fontSize: '1.5rem'
                  }}
                >
                  T
                </Avatar>
                
                <Box>
                  <Typography variant="h5" fontWeight="700" sx={{ mb: 0.5 }}>
                    Página de Teste Abas
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Teste de implementação de 10 abas • Página de Demonstração
                  </Typography>
                </Box>
              </Box>

              {/* Ações */}
              <Button
                variant="contained"
                startIcon={<Add />}
                sx={{ borderRadius: 2 }}
              >
                Nova Ação
              </Button>
            </Box>
          </Paper>

          {/* Informações em Cards */}
          <Box 
            sx={{ 
              display: 'flex', 
              gap: 1, 
              overflowX: 'auto',
              flexWrap: { xs: 'nowrap', md: 'wrap' },
              '&::-webkit-scrollbar': {
                height: 6
              },
              '&::-webkit-scrollbar-track': {
                borderRadius: 3,
                bgcolor: 'grey.100'
              },
              '&::-webkit-scrollbar-thumb': {
                borderRadius: 3,
                bgcolor: 'grey.300'
              }
            }}
          >
            {/* Total de Abas */}
            <Paper 
              elevation={0} 
              sx={{ 
                p: 1, 
                minWidth: { xs: 100, sm: 120, md: 140 },
                flex: { xs: '0 0 auto', md: '1 1 0' },
                bgcolor: 'primary.50',
                border: '1px solid',
                borderColor: 'primary.200',
                borderRadius: 2,
                transition: 'all 0.2s ease',
                '&:hover': {
                  borderColor: 'primary.main',
                  bgcolor: 'primary.100'
                }
              }}
            >
              <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                <Assignment color="primary" sx={{ fontSize: 18 }} />
                <Typography variant="caption" fontWeight="600" color="text.secondary">
                  TOTAL DE ABAS
                </Typography>
              </Box>
              <Typography variant="h6" fontWeight="700" color="primary.main">
                10
              </Typography>
            </Paper>

            {/* Aba Ativa */}
            <Paper 
              elevation={0} 
              sx={{ 
                p: 1, 
                minWidth: { xs: 100, sm: 120, md: 140 },
                flex: { xs: '0 0 auto', md: '1 1 0' },
                bgcolor: 'success.50',
                border: '1px solid',
                borderColor: 'success.200',
                borderRadius: 2,
                transition: 'all 0.2s ease',
                '&:hover': {
                  borderColor: 'success.main',
                  bgcolor: 'success.100'
                }
              }}
            >
              <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                <Assignment color="success" sx={{ fontSize: 18 }} />
                <Typography variant="caption" fontWeight="600" color="text.secondary">
                  ABA ATIVA
                </Typography>
              </Box>
              <Typography variant="h6" fontWeight="700" color="success.main">
                {tabValue + 1}
              </Typography>
            </Paper>

            {/* Status */}
            <Paper 
              elevation={0} 
              sx={{ 
                p: 1, 
                minWidth: { xs: 100, sm: 120, md: 140 },
                flex: { xs: '0 0 auto', md: '1 1 0' },
                bgcolor: 'info.50',
                border: '1px solid',
                borderColor: 'info.200',
                borderRadius: 2,
                transition: 'all 0.2s ease',
                '&:hover': {
                  borderColor: 'info.main',
                  bgcolor: 'info.100'
                }
              }}
            >
              <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                <Assignment color="info" sx={{ fontSize: 18 }} />
                <Typography variant="caption" fontWeight="600" color="text.secondary">
                  STATUS
                </Typography>
              </Box>
              <Chip
                label="Ativo"
                color="success"
                size="small"
                variant="outlined"
              />
            </Paper>
          </Box>
        </Box>

        {/* Abas */}
        <Paper elevation={2}>
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs
              value={tabValue}
              onChange={handleTabChange}
              variant="scrollable"
              scrollButtons="auto"
              aria-label="Abas de teste"
              sx={{
                minHeight: 40,
                '& .MuiTabs-scroller': {
                  display: 'flex',
                  justifyContent: 'flex-start'
                },
                '& .MuiTabs-flexContainer': {
                  justifyContent: 'flex-start'
                },
                '& .MuiTabs-indicator': {
                  display: 'none'
                },
                '& .MuiTab-root': {
                  minHeight: 40,
                  fontSize: { xs: '0.7rem', sm: '0.75rem', md: '0.8rem' },
                  padding: { xs: '6px 6px', sm: '6px 8px', md: '6px 12px' },
                  margin: '4px 2px 0 2px',
                  borderRadius: '8px 8px 0 0',
                  backgroundColor: 'transparent',
                  color: 'text.secondary',
                  transition: 'all 0.2s ease',
                  minWidth: { xs: 'auto', sm: 'auto', md: 'auto' },
                  maxWidth: { xs: '120px', sm: '140px', md: '160px' },
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  '&:hover': {
                    backgroundColor: 'rgba(25, 118, 210, 0.04)',
                    color: 'primary.main'
                  },
                  '&.Mui-selected': {
                    backgroundColor: 'rgba(25, 118, 210, 0.08)',
                    color: 'primary.main',
                    fontWeight: 600
                  }
                }
              }}
            >
              {tabData.map((tab, index) => (
                <Tab
                  key={index}
                  label={tab.title}
                  icon={tab.icon}
                  iconPosition="start"
                  {...a11yProps(index)}
                />
              ))}
            </Tabs>
          </Box>

          {/* Conteúdo das Abas */}
          {tabData.map((tab, index) => (
            <TabPanel key={index} value={tabValue} index={index}>
              <Box>
                {/* Header da Aba */}
                <Box sx={{ mb: 3 }}>
                  <Box display="flex" alignItems="flex-start" gap={2} mb={2}>
                    <Avatar 
                      sx={{ 
                        bgcolor: `${tab.color}.main`,
                        width: 48,
                        height: 48,
                        flexShrink: 0
                      }}
                    >
                      {tab.icon}
                    </Avatar>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="h5" fontWeight="600" color={`${tab.color}.main`} sx={{ mb: 1 }}>
                        {tab.title}
                      </Typography>
                      <Typography 
                        variant="body2" 
                        color="text.secondary"
                        sx={{ 
                          wordBreak: 'break-word',
                          lineHeight: 1.4
                        }}
                      >
                        {tab.description}
                      </Typography>
                    </Box>
                  </Box>
                  
                  <Divider sx={{ mb: 3 }} />
                </Box>

                {/* Conteúdo da Aba */}
                <Grid container spacing={2}>
                  {/* Card Principal */}
                  <Grid item xs={12} lg={8} md={7}>
                    <Card elevation={2} sx={{ height: '100%' }}>
                      <CardContent>
                        <Typography variant="h6" gutterBottom>
                          Conteúdo Principal
                        </Typography>
                        <Typography variant="body1" paragraph>
                          {tab.content}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Esta é uma demonstração do conteúdo da aba {index + 1}. 
                          Aqui você pode adicionar qualquer tipo de conteúdo, como formulários, 
                          tabelas, gráficos ou outros componentes.
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>

                  {/* Card Lateral */}
                  <Grid item xs={12} lg={4} md={5}>
                    <Card elevation={2} sx={{ height: '100%' }}>
                      <CardContent>
                        <Typography variant="h6" gutterBottom>
                          Informações
                        </Typography>
                        <Box sx={{ mb: 2 }}>
                          <Typography variant="body2" color="text.secondary" gutterBottom>
                            Número da Aba:
                          </Typography>
                          <Chip 
                            label={`Aba ${index + 1}`} 
                            color={tab.color as any}
                            variant="outlined"
                          />
                        </Box>
                        <Box sx={{ mb: 2 }}>
                          <Typography variant="body2" color="text.secondary" gutterBottom>
                            Status:
                          </Typography>
                          <Chip 
                            label="Ativo" 
                            color="success" 
                            size="small"
                          />
                        </Box>
                        <Box>
                          <Typography variant="body2" color="text.secondary" gutterBottom>
                            Tipo:
                          </Typography>
                          <Chip 
                            label="Teste" 
                            color="info" 
                            size="small"
                            variant="outlined"
                          />
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>

                  {/* Cards de Ação */}
                  <Grid item xs={12}>
                    <Card elevation={1}>
                      <CardContent>
                        <Typography variant="h6" gutterBottom>
                          Ações Disponíveis
                        </Typography>
                        <Box display="flex" gap={2} flexWrap="wrap">
                          <Button
                            variant="contained"
                            startIcon={<Add />}
                            color={tab.color as any}
                          >
                            Adicionar Item
                          </Button>
                          <Button
                            variant="outlined"
                            startIcon={<Edit />}
                          >
                            Editar
                          </Button>
                          <Button
                            variant="outlined"
                            startIcon={<Assignment />}
                          >
                            Ver Detalhes
                          </Button>
                          <Button
                            variant="outlined"
                            startIcon={<Assignment />}
                          >
                            Histórico
                          </Button>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
              </Box>
            </TabPanel>
          ))}
        </Paper>
      </Box>
    </>
  );
};

export default TesteAbasPage; 